import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getTimestampsForFirstAndLastPayer } from "../../app/04_Analytics/api/apiSaas";
import { RootStateI } from "../index";

interface MonthlyStatisticsData {
  sts: number;
  ets: number;
}

export interface StatisticsStateI {
  monthlyStatistics: {
    data: MonthlyStatisticsData | null;
    isLoading: boolean;
    error: string | null;
    lastFetchedAt: number | null;
  };
}

const initialState: StatisticsStateI = {
  monthlyStatistics: {
    data: null,
    isLoading: false,
    error: null,
    lastFetchedAt: null
  }
};

export const fetchMonthlyStatistics = createAsyncThunk(
  "statistics/fetchMonthlyStatistics",
  async (_, { getState }) => {
    const state = getState() as RootStateI;
    const accountToDisplay = state.user.accountToDisplay;
    const token = state.user.masterToken;

    if (!token || !accountToDisplay?.trackingID) {
      return { sts: 0, ets: 0 };
    }

    const data = await getTimestampsForFirstAndLastPayer(
      accountToDisplay.trackingID,
      token
    );

    return data;
  }
);

const statisticsSlice = createSlice({
  name: "statistics",
  initialState,
  reducers: {
    clearMonthlyStatistics: (state) => {
      state.monthlyStatistics = initialState.monthlyStatistics;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMonthlyStatistics.pending, (state) => {
        state.monthlyStatistics.isLoading = true;
        state.monthlyStatistics.error = null;
      })
      .addCase(
        fetchMonthlyStatistics.fulfilled,
        (state, action: PayloadAction<MonthlyStatisticsData>) => {
          state.monthlyStatistics.isLoading = false;
          state.monthlyStatistics.data = action.payload;
          state.monthlyStatistics.lastFetchedAt = Date.now();
          state.monthlyStatistics.error = null;
        }
      )
      .addCase(fetchMonthlyStatistics.rejected, (state, action) => {
        state.monthlyStatistics.isLoading = false;
        state.monthlyStatistics.error =
          action.error.message || "Failed to fetch monthly statistics";
      });
  }
});

export const { clearMonthlyStatistics } = statisticsSlice.actions;

export const selectMonthlyStatistics = (state: RootStateI) =>
  state.statistics.monthlyStatistics;

export default statisticsSlice.reducer;
